import React from "react";
import { useTranslation } from "react-i18next";
import { Button, ErrorAlert, Field, InfoField } from "src/components";
import PaymentLimits from "src/components/paymentLimits/PaymentLimits";
import { useDeposit } from "src/hooks";
import { formatNumberToLocale, getCurrencyDefaultPrecision } from "src/utils";
import "./deposit-form.scss";

// Ozow instant-buy: fiat pay currency for conversion routes (USDT wallet → ZAR pay).
const OZOW_CONVERSION_PAY_CURRENCY: Record<string, string> = {
  USDT: "ZAR",
};

const DepositForm: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    amount,
    amountTouched,
    setAmountTouched,
    currency,
    conversionFlowEnabled,
    ozowEstimateQuote,
    ozowEstimateQuoteLoading,
    paymentFee,
    paymentFeeLoading,
    limitsStatus,
    availableLimit,
    limitsLoading,
    handleAmountChange,
    handleInitiateDeposit,
    initiateDepositLoading,
    initiateDepositError,
    errors,
    submitDisabled,
  } = useDeposit();

  const { currency_id, precision } = currency;
  const buttonError = Object.values(errors).flat()[0];
  const showConversionRate = !!ozowEstimateQuote?.price;
  const defaultPayCurrencyId = OZOW_CONVERSION_PAY_CURRENCY[currency_id] ?? currency_id;
  const targetCurrencyId = ozowEstimateQuote?.target_currency_id ?? currency_id;
  const sourceCurrencyId = ozowEstimateQuote?.source_currency_id ?? defaultPayCurrencyId;
  const targetCurrencyPrecision = getCurrencyDefaultPrecision(targetCurrencyId);
  const sourceCurrencyPrecision = getCurrencyDefaultPrecision(sourceCurrencyId);
  const feeCurrencyId = ozowEstimateQuote?.fee.currency_id ?? targetCurrencyId;
  const feeCurrencyPrecision = getCurrencyDefaultPrecision(feeCurrencyId);
  const receiveAmount = Number(amount) - Number(paymentFee);

  return (
    <form className="deposit-form" onSubmit={handleInitiateDeposit}>
      <Field
        label={t("DEPOSIT_FORM.AMOUNT")}
        value={amount}
        onChange={(value) => handleAmountChange(value)}
        placeholder={t("DEPOSIT_FORM.AMOUNT_PLACEHOLDER_ZERO")}
        disabled={initiateDepositLoading}
        error={!conversionFlowEnabled && amountTouched ? errors.inputErrors[0] : undefined}
        onBlur={!conversionFlowEnabled ? () => setAmountTouched(true) : undefined}
      />

      {conversionFlowEnabled ? (
        <>
          <InfoField
            label={t("DEPOSIT_FORM.CONVERSION_RECEIVE")}
            value={t("DEPOSIT_FORM.CONVERSION_RECEIVE_VALUE", {
              amount: formatNumberToLocale(ozowEstimateQuote?.total_to_receive ?? 0, targetCurrencyPrecision),
              currency: targetCurrencyId,
            })}
            isLoading={ozowEstimateQuoteLoading}
          />

          <InfoField
            label={t("DEPOSIT_FORM.CONVERSION_PAY")}
            value={t("DEPOSIT_FORM.CONVERSION_PAY_VALUE", {
              amount: formatNumberToLocale(ozowEstimateQuote?.source_currency_amount ?? 0, sourceCurrencyPrecision),
              currency: sourceCurrencyId,
            })}
            isLoading={ozowEstimateQuoteLoading}
          />

          <InfoField
            label={t("DEPOSIT_FORM.FEE")}
            value={`${formatNumberToLocale(ozowEstimateQuote?.fee.amount ?? 0, feeCurrencyPrecision)} ${feeCurrencyId}`}
            isLoading={ozowEstimateQuoteLoading}
          />

          {showConversionRate && ozowEstimateQuote && (
            <InfoField
              label={t("DEPOSIT_FORM.CONVERSION_RATE")}
              value={t("DEPOSIT_FORM.CONVERSION_RATE_VALUE", {
                rate: formatNumberToLocale(ozowEstimateQuote.price, sourceCurrencyPrecision),
                sourceCurrency: sourceCurrencyId,
                targetCurrency: targetCurrencyId,
              })}
              isLoading={ozowEstimateQuoteLoading}
            />
          )}

          <p className="deposit-form-disclaimer">
            {t("DEPOSIT_FORM.CONVERSION_USDT_DISCLAIMER", {
              defaultValue:
                "Total to receive is approximate. The exact USDT amount is determined at settlement based on the live market rate.",
            })}
          </p>
        </>
      ) : (
        <>
          <InfoField
            label={t("DEPOSIT_FORM.FEE")}
            value={`${formatNumberToLocale(paymentFee, precision)} ${currency_id}`}
            isLoading={paymentFeeLoading}
          />

          <InfoField
            label={t("DEPOSIT_FORM.RECEIVE")}
            value={`${formatNumberToLocale(receiveAmount, precision)} ${currency_id}`}
            isLoading={paymentFeeLoading}
          />
        </>
      )}

      <PaymentLimits
        limitsStatus={limitsStatus}
        availableLimit={availableLimit}
        currency={currency_id}
        isLoading={limitsLoading}
      />

      <ErrorAlert message={initiateDepositError} />

      <Button
        title={t("DEPOSIT_FORM.NEXT_STEP")}
        className="deposit-button"
        type="submit"
        disabled={submitDisabled}
        pending={initiateDepositLoading}
        tooltipMessage={buttonError}
      />
    </form>
  );
};

export default DepositForm;
